import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { CheckoutLifecycleEventPostMessageName } from './CheckoutLifecycleEventPostMessageName';

export enum Platform {
    ios = 'ios',
    android = 'android',
    web = 'web',
    reactNative = 'reactNative',
}

export const isWebPlatform = (
    requestedPlatform: Platform,
): requestedPlatform is Platform.web => requestedPlatform === Platform.web;

const isReactNativeWebView = () => {
    return (
        typeof window !== 'undefined' &&
        (window as any).ReactNativeWebView !== undefined
    );
};

interface MessageDictionary extends Record<string, unknown> {
    messageName: CheckoutLifecycleEventPostMessageName;
    eventId?: string;
}

interface NativeiOSBridge<Payload> {
    postMessage: (payload: Payload) => void;
}

const getTypedWindow = () =>
    window as unknown as {
        webkit?: {
            messageHandlers?: {
                ffi?: NativeiOSBridge<string | Record<string, unknown>>;
                postDictionary?: NativeiOSBridge<MessageDictionary>;
            };
        };
        android?: {
            postDictionary?: (dictionary: MessageDictionary) => void;
            postMessage: (message: string) => void;
        };
    };

export interface PostMessageMessage {
    additionalData?: Record<string, unknown>;
    eventId?: string;
    messageName: CheckoutLifecycleEventPostMessageName;
}

export interface PostMessageOptions {
    sendDictionaryToiOS?: boolean;
}

const getMessageDictionary = ({
    additionalData,
    eventId,
    messageName,
}: PostMessageMessage): MessageDictionary => ({
    ...additionalData,
    messageName,
    eventId,
});

const sendMessageToiOSNativeApp = (
    { additionalData, eventId, messageName }: PostMessageMessage,
    options: Pick<PostMessageOptions, 'sendDictionaryToiOS'>,
) => {
    const typedWindow = getTypedWindow();

    if (typedWindow.webkit?.messageHandlers?.postDictionary) {
        const messageDictionary = getMessageDictionary({
            additionalData,
            messageName,
            eventId,
        });

        typedWindow.webkit.messageHandlers.postDictionary.postMessage(
            messageDictionary,
        );

        return true;
    }

    if (typedWindow.webkit?.messageHandlers?.ffi) {
        if (options.sendDictionaryToiOS) {
            typedWindow.webkit.messageHandlers.ffi.postMessage({
                message: {
                    messageName,
                    additionalData,
                },
            });
        } else {
            typedWindow.webkit.messageHandlers.ffi.postMessage(messageName);
        }

        return true;
    }

    return false;
};

const sendMessageToAndroidNativeApp = ({
    additionalData,
    eventId,
    messageName,
}: PostMessageMessage) => {
    const typedWindow = getTypedWindow();

    if (typedWindow.android?.postDictionary) {
        const messageDictionary = getMessageDictionary({
            additionalData,
            messageName,
            eventId,
        });

        typedWindow.android?.postDictionary(messageDictionary);
        return true;
    }

    if (typedWindow.android?.postMessage) {
        typedWindow.android?.postMessage(messageName);
        return true;
    }

    return false;
};

const sendMessageToReactNativeWebView = ({
    additionalData,
    eventId,
    messageName,
}: PostMessageMessage) => {
    if (isReactNativeWebView()) {
        const messageDictionary = getMessageDictionary({
            additionalData,
            messageName,
            eventId,
        });

        (window as any).ReactNativeWebView.postMessage(
            JSON.stringify(messageDictionary),
        );
        return true;
    }

    return false;
};

export const checkoutPostMessage = (
    {
        additionalData = {},
        eventId,
        messageName,
        widgetParentUrl,
    }: PostMessageMessage & {
        widgetParentUrl?: string;
    },
    options?: PostMessageOptions,
): Platform | undefined => {
    if (!HAS_WINDOW) {
        return;
    }

    if (
        sendMessageToiOSNativeApp(
            {
                additionalData,
                eventId,
                messageName,
            },
            {
                sendDictionaryToiOS: options?.sendDictionaryToiOS,
            },
        )
    ) {
        return Platform.ios;
    }

    if (
        sendMessageToAndroidNativeApp({
            additionalData,
            eventId,
            messageName,
        })
    ) {
        return Platform.android;
    }

    if (
        sendMessageToReactNativeWebView({
            additionalData,
            eventId,
            messageName,
        })
    ) {
        return Platform.reactNative;
    }

    // When testing Checkout we sometimes access it directly through
    // `/checkout-external`, without passing a parent URL
    // We do not want this helper to crash in that situation, so we just
    // check the parent url before sending a postMessage
    if (widgetParentUrl) {
        const messageDictionary = getMessageDictionary({
            additionalData,
            messageName,
            eventId,
        });

        window.parent?.postMessage(messageDictionary, widgetParentUrl);
    }

    return Platform.web;
};
